import { copaService } from 'js/vue/services/copa.service'
import { ref, watch } from 'vue'
import { NotifyAlert } from '../boot/Notify'
import { paramsJSON } from 'store/params.store'
import {
  copaLoading,
  levelAccess,
  tierList,
  comparationItems,
  activeFilter,
  availableFilters,
  filtersLoading,
  filterModel,
  enablers,
  summaryCards,
  summaryCardsLoading,
  filterParams,
  evolutionSeries,
  evolutionSeriesLoading,
  quickWinsHighlightsData,
  quickWinsHighlightsLoading,
} from 'store/copa.stores'

/**
 * @description Composable para gerenciar relatórios e dados do dashboard Copa
 * @returns {Object} - Objeto com funções e estados para manipulação dos relatórios
 */
export default function useCopaReports() {
  const {
    getAuth,
    getReportsOverview,
    getReportsEnablers,
    getReportsSummary,
    getReportsEvolution,
    getReportsQuickWins,
    getReportsHighlights,
    getZonesList,
    getBottlersList,
    getTierList,
  } = copaService()

  /**
   * @description Formata um número float para exibição, substituindo ponto por vírgula
   * @param {number} num - Número a ser formatado
   * @returns {number|string} - Número inteiro ou string formatada com uma casa decimal
   */
  const formatFloatNumber = (num) =>
    Number.isInteger(num)
      ? num
      : Number(num).toFixed(1).replace('.', ',')

  /**
   * @description Formata as linhas do overview para exibição na tabela
   * @param {Array<Object>} items - Lista de itens do overview
   * @returns {Array<Object>} - Lista formatada com category, possible_points, current_points, gap_oportunity e gap_bench_latam
   */
  const formatOverviewRows = (items) =>
    items.map((item) => ({
      category: item.name,
      possible_points: formatFloatNumber(item.peso),
      current_points: formatFloatNumber(item.value),
      gap_oportunity: formatFloatNumber(item.gap_peso),
      gap_bench_latam: formatFloatNumber(item.gap_latam),
    }))

  /**
   * @description Busca os dados de overview do relatório
   * @param {Object} params - Parâmetros de filtro para a requisição
   * @returns {Promise<Object|undefined>} - Objeto com name e rows formatadas ou undefined em caso de erro
   */
  async function fetchOverview(params) {
    try {
      const response = await getReportsOverview(params)
      const result = response.results.at()

      if (!result) return

      return {
        name: result.fab_name,
        fabName: result.latam_fab_name,
        rows: formatOverviewRows(result.items),
      }
    } catch (error) {
      console.error('Error fetching overview:', error)
    }
  }

  /**
   * @description Busca os dados de enablers e atualiza a store
   * @param {Object} params - Parâmetros de filtro para a requisição
   * @returns {Promise<void>}
   */
  async function fetchEnablers(params = filterParams.value) {
    try {
      const response = await getReportsEnablers(params)
      enablers.value = response?.results || []
    } catch (error) {
      console.error('Error fetching enablers:', error)
    }
  }

  /**
   * @description Busca as opções de filtros (zonas e bottlers) disponíveis
   * @param {Object} params - Parâmetros de filtro para a requisição
   * @returns {Promise<Object|undefined>} - Objeto com zones, bottlers e both (combinação de ambos) ou undefined em caso de erro
   */
  async function fetchFiltersOptions(params) {
    try {
      filtersLoading.value = true

      const zonesResponse = await getZonesList(params)
      const bottlersResponse = await getBottlersList(params)

      const zones = zonesResponse?.results?.map((i) => ({
        label: i.fab_zone,
        value: i.fab_zone,
        filter: 'fab_zone',
      }))

      const bottlers = bottlersResponse?.results?.map((i) => ({
        label: i.fab_name,
        value: i.fab_code,
        filter: 'fab_code',
      }))

      const both = [...(zones || []), ...(bottlers || [])]

      filtersLoading.value = false

      return { zones, bottlers, both }
    } catch (error) {
      console.error('Error fetching filter options:', error)
    }
  }

  /**
   * @description Formata os cards de resumo para exibição
   * @param {Array<Object>} items - Lista de itens do resumo
   * @returns {Array<Object>} - Lista formatada com key, title, subtitle e value
   */
  const formatSummaryCards = (items) =>
    items.map(({ key, value, title, subtitle, icon }) => ({
      key,
      title,
      subtitle,
      icon,
      value:
        typeof value === 'number' ? formatFloatNumber(value) : value,
    }))

  /**
   * @description Busca os dados de resumo e atualiza a store summaryCards
   * @param {Object} [params=filterParams.value] - Parâmetros de filtro para a requisição
   * @returns {Promise<void>}
   */
  async function fetchSummary(params = filterParams.value) {
    try {
      summaryCardsLoading.value = true
      const response = await getReportsSummary(params)
      const result = response.results.at()

      if (!result) {
        summaryCards.value = []
        summaryCardsLoading.value = false
        throw new Error('No summary data found')
      }

      if (result.items.length > 4) {
        const filteredItems = result.items.filter(
          (i) => i.key !== 'min'
        )
        summaryCards.value = formatSummaryCards(filteredItems) || []
      } else {
        summaryCards.value = formatSummaryCards(result.items) || []
      }

      summaryCardsLoading.value = false
    } catch (error) {
      console.error('Error fetching summary:', error)
    }
  }

  /**
   * @description Extrai os valores de evolução do response
   * @param {Object} response - Response da API
   * @returns {Array<number>} - Array com os valores de cada período
   */
  const extractEvolutionData = (response) =>
    response?.results?.map((r) => {
      const value = r.items?.[0]?.value
      return value.toFixed(1) || 0
    }) || []

  /**
   * @description Busca os dados de evolução e atualiza a store evolutionSeries
   * @param {Object} [params=filterParams.value] - Parâmetros de filtro para a requisição
   * @returns {Promise<void>}
   */
  async function fetchEvolution(params = filterParams.value) {
    try {
      evolutionSeriesLoading.value = true

      const series = []
      const {
        fab_tier,
        fab_tier_type,
        fab_zone,
        fab_code,
        bottler,
        both_tier_types,
      } = params

      // Se tiver fab_code, faz apenas uma requisição com o name do code
      if (fab_code) {
        const response = await getReportsEvolution({ fab_code })
        series.push({
          name: bottler,
          data: extractEvolutionData(response),
        })
      }
      // Se tiver zona, faz apenas uma requisição com o name da zona
      else if (fab_zone) {
        const response = await getReportsEvolution({ fab_zone })
        series.push({
          name: fab_zone,
          data: extractEvolutionData(response),
        })
      }
      // Se fab_tier for 2, faz apenas uma requisição com name 'W/O SOV'
      else if (fab_tier === 2) {
        const response = await getReportsEvolution({ fab_tier: 2 })
        series.push({
          name: 'W/O SOV',
          data: extractEvolutionData(response),
        })
      }
      // Se fab_tier for 1 e ambos os tipos selecionados, faz duas requisições
      else if (fab_tier === 1 && both_tier_types) {
        const [responseA, responseB] = await Promise.all([
          getReportsEvolution({ fab_tier: 1, fab_tier_type: 'A' }),
          getReportsEvolution({ fab_tier: 1, fab_tier_type: 'B' }),
        ])

        series.push(
          {
            name: 'MMUC > 150',
            data: extractEvolutionData(responseA),
          },
          {
            name: 'MMUC <= 150',
            data: extractEvolutionData(responseB),
          }
        )
      }
      // Se fab_tier for 1 e não tiver fab_tier_type
      else if (fab_tier === 1 && !fab_tier_type) {
        const response = await getReportsEvolution({
          fab_tier: 1,
        })
        series.push({
          name: 'MMUC',
          data: extractEvolutionData(response),
        })
      }
      // Se fab_tier for 1 e fab_tier_type for A
      else if (fab_tier === 1 && fab_tier_type === 'A') {
        const response = await getReportsEvolution({
          fab_tier: 1,
          fab_tier_type: 'A',
        })
        series.push({
          name: 'MMUC > 150',
          data: extractEvolutionData(response),
        })
      }
      // Se fab_tier for 1 e fab_tier_type for B
      else if (fab_tier === 1 && fab_tier_type === 'B') {
        const response = await getReportsEvolution({
          fab_tier: 1,
          fab_tier_type: 'B',
        })
        series.push({
          name: 'MMUC <= 150',
          data: extractEvolutionData(response),
        })
      }

      evolutionSeries.value = series.map((s) => ({
        ...s,
        fixed: true,
      }))

      evolutionSeriesLoading.value = false
    } catch (error) {
      console.error('Error fetching evolution:', error)
    }
  }

  /**
   * @description Busca os dados de QuickWins e Highlights para um fab_code
   * @param {string} fabCode - Código do fabricante
   * @param {boolean} isPriority - Se é uma requisição prioritária
   * @returns {Promise<Object|null>} - Objeto com fab_code, quickWins e highlights ou null em caso de erro
   */
  async function fetchQuickWinsHighlights(
    fabCode,
    isPriority = false
  ) {
    try {
      const [quickWinsResponse, highlightsResponse] =
        await Promise.all([
          getReportsQuickWins({ fab_code: fabCode }),
          getReportsHighlights({ fab_code: fabCode }),
        ])

      return {
        fab_code: fabCode,
        fab_name: quickWinsResponse?.fab_name || '',
        current_points:
          quickWinsResponse?.current_points?.toFixed(1) || 0,
        future_points:
          quickWinsResponse?.future_points?.toFixed(1) || 0,
        quickWins: quickWinsResponse?.items || [],
        highlights: highlightsResponse || [],
        isPriority,
      }
    } catch (error) {
      console.error(
        'Error fetching quick wins and highlights:',
        error
      )
      return null
    }
  }

  /**
   * @description Atualiza os dados de QuickWins e Highlights baseado no fab_code do dashboard
   * @param {string|null} dashboardFabCode - fab_code do filtro do dashboard
   * @param {boolean} isPriority - Se é uma requisição prioritária
   * @param {string|null} fabZone - fab_zone para buscar lista de fabricantes
   * @returns {Promise<void>}
   */
  async function updateQuickWinsHighlights(
    dashboardFabCode = null,
    isPriority = false,
    fabZone = null
  ) {
    quickWinsHighlightsLoading.value = true

    try {
      // Se isPriority false e tem fabZone, busca lista de fabricantes
      if (!isPriority && fabZone) {
        quickWinsHighlightsData.value = [
          {
            fab_code: null,
            fab_zone: fabZone,
            fab_name: null,
            isPriority: false,
            showCards: false,
          },
        ]
        return
      }

      // Se nenhum fab_code foi fornecido, limpa os dados
      if (!dashboardFabCode) {
        quickWinsHighlightsData.value = []
        return
      }

      // Apenas um fab_code (dashboard)
      if (dashboardFabCode) {
        const data = await fetchQuickWinsHighlights(
          dashboardFabCode,
          isPriority
        )
        if (data) quickWinsHighlightsData.value = [data]
      }
    } catch (error) {
      console.error(
        'Error updating quick wins and highlights:',
        error
      )
    } finally {
      quickWinsHighlightsLoading.value = false
    }
  }

  // Filtros coluna extra / "Comparar com"
  const search = ref('')

  /**
   * @description Verifica se um filtro está desabilitado com base na disponibilidade
   * @param {string} filter - Nome do filtro ('Zona' ou 'Bottler')
   * @returns {boolean} - true se o filtro estiver desabilitado, false caso contrário
   */
  const isFilterDisabled = (filter) => {
    if (filter === 'Zona') return !availableFilters.value.zones
    if (filter === 'Bottler') return !availableFilters.value.bottlers
    return false
  }

  /**
   * @description Carrega os itens de filtro com base no filtro ativo e termo de busca
   * @returns {Promise<void>}
   */
  const loadItems = async () => {
    const response = await fetchFiltersOptions({
      search: search.value,
      fab_tier: filterParams.value.fab_tier,
      fab_tier_type: filterParams.value.fab_tier_type,
    })

    // Verificar disponibilidade dos filtros
    availableFilters.value.zones =
      response?.zones && response.zones.length > 0
    availableFilters.value.bottlers =
      response?.bottlers && response.bottlers.length > 0

    // Se o filtro ativo ficou desabilitado, mudar para 'Tudo'
    if (
      activeFilter.value === 'Zona' &&
      !availableFilters.value.zones
    ) {
      activeFilter.value = 'Tudo'
    } else if (
      activeFilter.value === 'Bottler' &&
      !availableFilters.value.bottlers
    ) {
      activeFilter.value = 'Tudo'
    }

    if (activeFilter.value === 'Tudo') {
      comparationItems.value = response?.both || []
    } else if (activeFilter.value === 'Zona') {
      comparationItems.value = response?.zones || []
    } else if (activeFilter.value === 'Bottler') {
      comparationItems.value = response?.bottlers || []
    }
  }

  /**
   * @description Adiciona uma comparação ao gráfico de evolução
   * @param {Object} item - Item selecionado para comparação
   * @param {string} item.label - Nome para exibir na série
   * @param {string} item.value - Valor do filtro
   * @param {string} item.filter - Tipo do filtro (fab_code, fab_zone, etc)
   * @returns {Promise<boolean>} - true se foi adicionado, false se já existe ou limite atingido
   */
  async function addComparisonEvolution(item) {
    try {
      // Verificar se já existe uma série com o mesmo name
      const existingItem = evolutionSeries.value.find(
        (s) => s.name === item.label
      )
      if (existingItem) {
        NotifyAlert(`${item.label} já está sendo comparado.`)
        return false
      }

      // Verificar limite de 5 comparações adicionais (fixed = false)
      const comparisonCount = evolutionSeries.value.filter(
        (s) => !s.fixed
      ).length
      if (comparisonCount >= 5) {
        NotifyAlert('Limite máximo de 5 comparações atingido.')
        return false
      }

      // Fazer requisição
      const params = { [item.filter]: item.value }
      const response = await getReportsEvolution(params)

      // Adicionar nova série
      evolutionSeries.value.push({
        name: item.label,
        data: extractEvolutionData(response),
        fixed: false,
      })

      return true
    } catch (error) {
      console.error('Error adding comparison evolution:', error)
      return false
    }
  }

  /**
   * @description Adiciona uma comparação ao gráfico de evolução baseada no melhor desempenho
   * @returns {Promise<Object|boolean>} - Objeto com fabName se adicionado, false em caso de erro
   */
  async function addComparisonEvolutionBest(type) {
    try {
      const fabZone =
        filterModel.value.fabricante?.fab_zone ||
        filterModel.value.zona?.fab_zone ||
        ''

      const finalParams = {
        fab_tier: filterModel.value.tier,
        fab_code_best: '',
      }

      if (type === 'Melhor da região') {
        finalParams.fab_zone = fabZone
      }

      // Verificar limite de 5 comparações adicionais (fixed = false)
      const comparisonCount = evolutionSeries.value.filter(
        (s) => !s.fixed
      ).length

      if (comparisonCount >= 5) {
        NotifyAlert('Limite máximo de 5 comparações atingido.')
        return {}
      }

      // Fazer requisição
      const response = await getReportsEvolution(finalParams)
      const fabName = response.results.at()?.fab_name

      // Verificar se já existe uma série com o mesmo name
      const existingItem = evolutionSeries.value.find(
        (s) => s.name === fabName
      )
      if (existingItem) {
        NotifyAlert(`${type} já está sendo comparado.`)
        return {}
      }

      // Adicionar nova série
      evolutionSeries.value.push({
        name: fabName || 'Comparação',
        data: extractEvolutionData(response),
        fixed: false,
      })

      return { fabName }
    } catch (error) {
      console.error('Error adding comparison evolution best:', error)
      return false
    }
  }

  /**
   * @description Remove uma ou todas as comparações do gráfico de evolução
   * @param {number} [index] - Índice do item a ser removido. Se não informado, remove todos os itens com fixed = false
   * @returns {void}
   */
  function removeComparisonEvolution(index) {
    if (index !== undefined) {
      // Remove item específico por índice
      if (
        evolutionSeries.value[index] &&
        !evolutionSeries.value[index].fixed
      ) {
        evolutionSeries.value.splice(index, 1)
      }
    } else {
      // Remove todos os itens com fixed = false
      evolutionSeries.value = evolutionSeries.value.filter(
        (s) => s.fixed
      )
    }
  }

  watch([activeFilter, search], () => {
    loadItems()
  })

  /**
   * @description Atualiza todos os dados do dashboard usando os parâmetros de filtro da store
   * @param {Object} cardRef - Ref do componente CardPerformanceByCategory
   * @returns {Promise<void>}
   */
  async function refreshDashboard(cardRef = null) {
    const params = { ...filterParams.value }

    fetchTierList()
    fetchSummary(params)
    fetchEvolution(params)
    fetchEnablers(params)

    // Atualiza QuickWins e Highlights
    // Se tiver fab_zone sem fab_code, isPriority = false (mostra select)
    // Se tiver fab_code, isPriority = true (mostra direto)
    const dashboardFabCode = params.fab_code || null
    const hasFabZoneOnly = params.fab_zone && !params.fab_code
    const isPriority = !hasFabZoneOnly && !!dashboardFabCode

    if (dashboardFabCode || hasFabZoneOnly) {
      updateQuickWinsHighlights(
        dashboardFabCode,
        isPriority,
        params.fab_zone
      )
    }

    setTimeout(() => {
      cardRef?.value?.fetchInitialData(params)
    }, 500)
  }

  /**
   * @description Realiza a requisição de autenticação
   * @returns {Promise<void>}
   */
  async function requestAuth() {
    try {
      const response = await getAuth(paramsJSON.value)
      if (response?.level) {
        levelAccess.value = Number(response.level)
        copaLoading.value = false
      }
    } catch (error) {
      console.error('Error requesting auth:', error)
    }
  }

  /**
   * @description Busca a lista de tiers e atualiza a store tierList
   * @returns {Promise<void>}
   */
  async function fetchTierList() {
    try {
      const response = await getTierList({ t: 'global' })
      tierList.value = response?.results || []

      const findItem = (field, value) =>
        tierList.value.find((i) => i[field] === value)

      if (findItem('fab_tier', 1)) {
        filterModel.value.tier = 1

        if (
          findItem('fab_tier_type', 'A') &&
          findItem('fab_tier_type', 'B')
        ) {
          return
        }

        if (findItem('fab_tier_type', 'A')) {
          filterModel.value.type_a = true
        } else if (findItem('fab_tier_type', 'B')) {
          filterModel.value.type_b = true
        }
      } else if (findItem('fab_tier', 2)) {
        filterModel.value.tier = 2
      }
    } catch (error) {
      console.error('Error requesting tier list:', error)
    }
  }

  /**
   * @description Seleciona um fabricante e busca seus dados de QuickWins e Highlights
   * @param {string} fabCode - Código do fabricante selecionado
   * @param {number} itemIndex - Índice do item em quickWinsHighlightsData
   * @returns {Promise<void>}
   */
  async function selectBottlerForQuickWins(fabCode, itemIndex = 0) {
    if (!fabCode) return

    quickWinsHighlightsLoading.value = true

    try {
      const data = await fetchQuickWinsHighlights(fabCode, false)

      if (data && quickWinsHighlightsData.value[itemIndex]) {
        quickWinsHighlightsData.value[itemIndex] = {
          ...quickWinsHighlightsData.value[itemIndex],
          ...data,
          showCards: true,
        }
      }
    } catch (error) {
      console.error('Error selecting bottler:', error)
    } finally {
      quickWinsHighlightsLoading.value = false
    }
  }

  return {
    enablers,

    fetchOverview,
    fetchEnablers,
    fetchFiltersOptions,
    fetchSummary,
    fetchEvolution,
    fetchQuickWinsHighlights,
    updateQuickWinsHighlights,
    selectBottlerForQuickWins,
    addComparisonEvolution,
    addComparisonEvolutionBest,
    removeComparisonEvolution,
    refreshDashboard,
    requestAuth,

    search,
    isFilterDisabled,
    loadItems,
  }
}
